import * as React from 'react';
import Griddle, { RowDefinition, ColumnDefinition, GriddlePageProperties, GriddleSortKey } from 'griddle-react';
import * as GridConfig from '../../config/grid-config';
import './DataGrid.css';

interface PagedDataGridProps {
    dataFunction: (page: number, newPage: number, pageSize: number, filter?: string, sortProperty?: string, sortDirection?: string) => Promise<any>;
    columns: { id: string, title?: string, width?: string, customComponent?: ({ value }: any) => JSX.Element }[];
    defaultSort: GriddleSortKey;
    entities: any[];
    pageProperties: GriddlePageProperties;
    state?: any;
}

interface PagedDataGridState {
    filterTimer?: number;
    filterText: string;
    sortProperty: GriddleSortKey;
    state: any;
}

export class PagedDataGrid extends React.Component<PagedDataGridProps, PagedDataGridState> {
    public constructor(props: PagedDataGridProps) {
        super(props);

        this.state = {
            filterText: '',
            sortProperty: this.props.defaultSort,
            state: null
        };

        this.onFilter = this.onFilter.bind(this);
        this.onSort = this.onSort.bind(this);
        this.onNext = this.onNext.bind(this);
        this.onPrevious = this.onPrevious.bind(this);
        this.onGetPage = this.onGetPage.bind(this);
    }

    public componentDidMount() {
        let { currentPage, pageSize } = this.props.pageProperties!;
        this.props.dataFunction(currentPage! - 1, 1, pageSize!);
    }

    public componentWillUpdate() {
        if (this.props.state && this.state.state !== this.props.state) {
            this.setState({
                state: this.props.state
            });
            let { currentPage, pageSize } = this.props.pageProperties!;
            this.props.dataFunction(currentPage! - 1, 1, pageSize!);
        }
    }

    public getData(pageToLoad?: number, pageIndex?: number) {
        const { pageSize } = this.props.pageProperties!;
        const { filterText, sortProperty } = this.state;

        let property, direction;

        if (sortProperty) {
            property = sortProperty.id;
            direction = sortProperty.sortAscending ? 'asc' : 'desc';
        }

        if (pageToLoad === undefined || pageIndex === undefined) {
            pageToLoad = 0;
            pageIndex = 1;
        }

        this.props.dataFunction(pageToLoad!, pageIndex!, pageSize!, filterText, property, direction);
    }

    public onFilter(filterText: any) {
        const { filterTimer } = this.state;

        if (filterTimer) {
            clearTimeout(filterTimer);
        }

        const newTimer = setTimeout(this.getData.bind(this), 500);

        this.setState({ filterTimer: newTimer, filterText: filterText });
    }

    public onSort(newSortProperty: GriddleSortKey) {
        const { sortProperty } = this.state;

        if (newSortProperty.sortAscending === undefined) {
            newSortProperty.sortAscending = true;
        }

        if (sortProperty && sortProperty.id === sortProperty.id) {
            newSortProperty.sortAscending = !sortProperty.sortAscending;
        }

        this.setState({ sortProperty: newSortProperty }, () => { this.getData(); });
    }

    public onNext() {
        let { currentPage } = this.props.pageProperties!;

        // // currentPage is 1-indexed in griddle, so we can just use it to get the next page, and then increment it when setting state
        this.getData(currentPage!, currentPage! + 1);
    }

    public onPrevious() {
        let { currentPage } = this.props.pageProperties!;

        // // currentPage is 1-indexed in griddle, so we need to subtract 2 to get the prev page, and then subtract 1 from it when setting state
        this.getData(currentPage! - 2, currentPage! - 1);
    }

    public onGetPage(pageNumber: any) {
        this.getData(pageNumber - 1, pageNumber);
    }

    public render() {
        const { columns, entities } = this.props;
        const { sortProperty } = this.state;

        const events = {
            onFilter: this.onFilter,
            onSort: this.onSort,
            onPrevious: this.onPrevious,
            onNext: this.onNext,
            onGetPage: this.onGetPage
        };

        return (
            <Griddle
                data={entities}
                events={events}
                styleConfig={GridConfig.gridStyle}
                pageProperties={this.props.pageProperties}
                sortProperties={[sortProperty]}
                components={{ Layout: GridConfig.gridLayout }}
            >
                <RowDefinition>
                    {columns.map((column, index) => <ColumnDefinition key={`column-${index}`} {...column} />)}
                </RowDefinition>
            </Griddle>
        );
    }
}


// WEBPACK FOOTER //
// ./src/components/datagrid/PagedDataGrid.tsx